import {stripHtmlToPlainText} from 'app/utils/knowledgeCenter/faqPreview';
import type {FaqCategoryKey} from 'app/utils/knowledgeCenter/categories';

export interface FaqSearchableItem {
    name: string;
    title: string;
    text?: string;
    category?: string;
    popular?: boolean;
    sequence?: number;
}

export type FaqArticleLink = Pick<FaqSearchableItem, 'name' | 'title'>;

export type FaqsByCategory = Record<string, FaqSearchableItem[]>;

export interface FaqCategorySummary {
    category: FaqCategoryKey;
    count: number;
    label: string;
}

const sortBySequence = (a: FaqSearchableItem, b: FaqSearchableItem): number => {
    const seqA = a.sequence ?? 0;
    const seqB = b.sequence ?? 0;

    return seqA - seqB;
};

export const groupFaqsByCategory = (
    items: FaqSearchableItem[],
    categoryKeys: readonly FaqCategoryKey[],
    knownCategoryKeys: ReadonlySet<FaqCategoryKey>
): FaqsByCategory => {
    const grouped = categoryKeys.reduce<FaqsByCategory>((acc, key) => {
        acc[key] = [];
        return acc;
    }, {});

    items.forEach(item => {
        if (item.category && knownCategoryKeys.has(item.category)) {
            grouped[item.category].push(item);
        }
    });

    categoryKeys.forEach(key => {
        grouped[key].sort(sortBySequence);
    });

    return grouped;
};

export const getPopulatedFaqCategories = (
    faqsByCategory: FaqsByCategory,
    categoryOptions: readonly { key: FaqCategoryKey; label: string }[]
): FaqCategorySummary[] => categoryOptions.reduce<FaqCategorySummary[]>((acc, option) => {
    const count = faqsByCategory[option.key]?.length ?? 0;

    if (count > 0) {
        acc.push({category: option.key, count, label: option.label});
    }

    return acc;
}, []);

export const selectPopularFaqs = (items: FaqSearchableItem[]): FaqSearchableItem[] => items
    .filter(item => item.popular)
    .sort(sortBySequence);

const REGEXP_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/gu;

const escapeRegExp = (value: string): string => value.replace(REGEXP_SPECIAL_CHARS, '\\$&');

export const searchFaqs = (items: FaqSearchableItem[], query: string): FaqSearchableItem[] => {
    const trimmed = query.trim();

    if (!trimmed) {
        return items;
    }

    // eslint-disable-next-line require-unicode-regexp
    const pattern = new RegExp(escapeRegExp(trimmed), 'i');

    return items.filter(item => {
        if (item.title && pattern.test(item.title)) {
            return true;
        }

        if (!item.text) {
            return false;
        }

        return pattern.test(stripHtmlToPlainText(item.text));
    });
};
